import { Check } from 'lucide-react';
import { Button, Pill } from '@/shared/components';
import type { WorkspaceAdminSummary, WorkspaceSummaryResponse } from '@/shared/api';
import type { AdminInvitationResend, ResendState } from './useAdminInvitationResend';
import {
  ADMIN_STATE_LABEL,
  adminDisplayName,
  canResendInvitation,
} from './workspaceAdminPresentation';

interface Props {
  workspace: WorkspaceSummaryResponse;
  invitation: AdminInvitationResend;
}

/**
 * Die Administratoren eines Workspace mit ihrem Zustand. „Ausstehend" heißt eingeladen, Link noch
 * nicht eingelöst; genau dort steht „Einladung erneut senden".
 */
export function WorkspaceAdmins({ workspace, invitation }: Props) {
  return (
    <div className="grid min-w-0 gap-3">
      <AdminCount count={workspace.activeAdminCount} />
      {workspace.admins.length > 0 && (
        <ul className="grid min-w-0 gap-3">
          {workspace.admins.map((admin) => (
            <AdminRow
              key={admin.userId}
              workspace={workspace}
              admin={admin}
              invitation={invitation}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function AdminCount({ count }: { count: number }) {
  return count === 0 ? (
    <span className="text-sealwax text-sm font-medium">Keiner aktiv</span>
  ) : (
    <span className="text-ink text-sm tabular-nums">{count} aktiv</span>
  );
}

function AdminRow({ workspace, admin, invitation }: Props & { admin: WorkspaceAdminSummary }) {
  const name = adminDisplayName(admin);
  const state = invitation.stateOf(admin.userId);
  return (
    <li className="min-w-0">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <span className="text-ink wrap-break-word min-w-0 text-sm">{name}</span>
        <Pill variant={admin.state === 'Active' ? 'live' : 'warning'} size="sm">
          {ADMIN_STATE_LABEL[admin.state]}
        </Pill>
      </div>
      {admin.email && admin.email !== name && (
        <p className="text-ink-faint wrap-break-word mt-0.5 text-xs">{admin.email}</p>
      )}
      {canResendInvitation(workspace, admin) && (
        <div className="mt-2 grid justify-items-start gap-1">
          <Button
            type="button"
            variant="ghost"
            className="border-line min-h-11 border"
            loading={state.status === 'sending'}
            onClick={() => invitation.resend(workspace.id, admin.userId)}
          >
            Einladung erneut senden
          </Button>
          <ResendOutcome state={state} />
        </div>
      )}
    </li>
  );
}

function ResendOutcome({ state }: { state: ResendState }) {
  return (
    <p aria-live="polite" className="text-xs">
      {state.status === 'sent' && (
        <span className="text-sage inline-flex items-center gap-1">
          <Check size={12} aria-hidden />
          Neue Einladung verschickt — der vorige Link gilt nicht mehr.
        </span>
      )}
      {state.status === 'failed' && <span className="text-sealwax">{state.reason}</span>}
    </p>
  );
}
